import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Save, Send, CheckCircle, XCircle, Eye, EyeOff, Database, Download, HelpCircle } from 'lucide-react';

const AdminSettings = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [smtp, setSmtp] = useState({
    host: '', port: 587, user: '', password: '',
    sender_email: '', sender_name: 'IAASP Academy', enabled: false,
  });
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['superadmin', 'admin'].includes(user.role)) { navigate('/dashboard'); return; }
    loadSmtp();
    setTestEmail(user.email || '');
  }, [user, authLoading, navigate]);

  const loadSmtp = async () => {
    try {
      const res = await api.get('/settings/smtp');
      setSmtp(res.data);
    } catch { /* not configured yet */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!smtp.host.trim() || !smtp.user.trim() || !smtp.password.trim()) {
      toast.error('Host, usuario y contraseña son requeridos');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/settings/smtp', smtp);
      toast.success(res.data.message);
      setSmtp(prev => ({ ...prev, enabled: res.data.enabled }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) { toast.error('Ingresa un email de destino'); return; }
    setTesting(true);
    try {
      const res = await api.post('/settings/smtp/test', { email: testEmail });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar prueba');
    } finally { setTesting(false); }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const response = await api.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      link.setAttribute('download', `iaasp_backup_${now}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup descargado correctamente');
    } catch (err) {
      toast.error('Error al generar el backup');
    } finally { setBackingUp(false); }
  };

  const handleMigrate = async () => {
    if (!window.confirm('Esto migrará todas las imágenes existentes al almacenamiento persistente. ¿Continuar?')) return;
    setMigrating(true);
    try {
      const res = await api.post('/admin/migrate-uploads');
      const counts = Object.values(res.data.report || {}).reduce(
        (acc, v) => ({ scanned: acc.scanned + (v.scanned || 0), migrated: acc.migrated + (v.migrated || 0), missing: acc.missing + (v.missing || 0) }),
        { scanned: 0, migrated: 0, missing: 0 }
      );
      toast.success(`Migración: ${counts.migrated}/${counts.scanned} archivos migrados · ${counts.missing} faltantes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en migración');
    } finally { setMigrating(false); }
  };

  const handleCleanBroken = async () => {
    if (!window.confirm('Esto eliminará (pondrá en null) las URLs de imágenes que ya no se pueden cargar. Útil para limpiar imágenes rotas tras un despliegue en producción. ¿Continuar?')) return;
    setCleaning(true);
    try {
      const res = await api.post('/admin/clean-broken-images');
      const counts = Object.values(res.data.report || {}).reduce(
        (acc, v) => ({ scanned: acc.scanned + (v.scanned || 0), cleaned: acc.cleaned + (v.cleaned || 0) }),
        { scanned: 0, cleaned: 0 }
      );
      toast.success(`Limpieza: ${counts.cleaned} URLs rotas eliminadas de ${counts.scanned} revisadas`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al limpiar URLs rotas');
    } finally { setCleaning(false); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando...</div>;

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-settings-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-1 -ml-2 mb-3 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" />Dashboard
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Configuración del Sistema</h1>
          <p className="text-sm text-muted-foreground mt-1">Ajustes de notificaciones y correo electrónico</p>
        </div>

        {/* SMTP Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Configuración SMTP</CardTitle>
                  <CardDescription>Servicio de correo electrónico para notificaciones</CardDescription>
                </div>
              </div>
              {smtp.enabled ? (
                <Badge className="bg-green-100 text-green-700 gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />Activo
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <XCircle className="w-3.5 h-3.5" />Sin configurar
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Provider quick setup guide */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              <p className="font-semibold mb-1">Configuración por proveedor:</p>
              <p>📧 <strong>Gmail:</strong> host=smtp.gmail.com, puerto=587, activa "Contraseña de aplicación"</p>
              <p>📧 <strong>Outlook:</strong> host=smtp-mail.outlook.com, puerto=587</p>
              <p>📧 <strong>Yahoo:</strong> host=smtp.mail.yahoo.com, puerto=465</p>
              <p>📧 <strong>SendGrid:</strong> host=smtp.sendgrid.net, puerto=587, user=apikey</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Servidor SMTP (host) *</Label>
                <Input
                  value={smtp.host}
                  onChange={e => setSmtp(p => ({ ...p, host: e.target.value }))}
                  placeholder="smtp.gmail.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Puerto *</Label>
                <Input
                  type="number"
                  value={smtp.port}
                  onChange={e => setSmtp(p => ({ ...p, port: parseInt(e.target.value) || 587 }))}
                  placeholder="587"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Usuario / Email SMTP *</Label>
                <Input
                  value={smtp.user}
                  onChange={e => setSmtp(p => ({ ...p, user: e.target.value }))}
                  placeholder="tu@gmail.com"
                  type="email"
                  data-testid="smtp-user-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Contraseña / API Key *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={smtp.password}
                    onChange={e => setSmtp(p => ({ ...p, password: e.target.value }))}
                    placeholder="contraseña de aplicación o API key"
                    className="pr-10"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email Remitente</Label>
                <Input
                  value={smtp.sender_email}
                  onChange={e => setSmtp(p => ({ ...p, sender_email: e.target.value }))}
                  placeholder="noreply@iaaspacademy.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre Remitente</Label>
                <Input
                  value={smtp.sender_name}
                  onChange={e => setSmtp(p => ({ ...p, sender_name: e.target.value }))}
                  placeholder="IAASP Academy"
                />
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="gap-2 w-full" data-testid="save-smtp-btn">
              <Save className="w-4 h-4" />{saving ? 'Guardando...' : 'Guardar Configuración SMTP'}
            </Button>

            {/* Test email */}
            {smtp.host && (
              <div className="pt-4 border-t space-y-3">
                <h4 className="text-sm font-semibold">Enviar correo de prueba</h4>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    placeholder="destino@email.com"
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button onClick={handleTest} disabled={testing} variant="outline" className="gap-2 shrink-0" data-testid="test-smtp-btn">
                    <Send className="w-4 h-4" />{testing ? 'Enviando...' : 'Enviar prueba'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backup Card */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Database className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Respaldo del Sistema</CardTitle>
                <CardDescription>Descarga una copia completa de la base de datos y archivos</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800 space-y-1">
              <p>El backup incluye:</p>
              <ul className="ml-4 list-disc space-y-0.5">
                <li>Todas las colecciones de MongoDB (usuarios, cursos, certificados, etc.)</li>
                <li>Archivos subidos al servidor</li>
                <li>Archivo de manifiesto con fecha y detalle del backup</li>
              </ul>
            </div>
            <Button
              onClick={handleBackup}
              disabled={backingUp}
              className="gap-2 w-full"
              variant="outline"
              data-testid="download-backup-btn"
            >
              <Download className="w-4 h-4" />
              {backingUp ? 'Generando backup...' : 'Descargar Backup Completo'}
            </Button>
            <Button
              onClick={handleMigrate}
              disabled={migrating}
              className="gap-2 w-full"
              variant="outline"
              data-testid="migrate-uploads-btn"
            >
              <Database className="w-4 h-4" />
              {migrating ? 'Migrando...' : 'Migrar imágenes locales a storage persistente'}
            </Button>
            <Button
              onClick={handleCleanBroken}
              disabled={cleaning}
              className="gap-2 w-full"
              variant="outline"
              data-testid="clean-broken-btn"
            >
              <XCircle className="w-4 h-4" />
              {cleaning ? 'Limpiando...' : 'Limpiar URLs de imágenes rotas'}
            </Button>
            <p className="text-xs text-muted-foreground">
              <strong>Migrar</strong>: útil si tienes archivos locales antiguos.<br />
              <strong>Limpiar URLs rotas</strong>: úsalo en producción después de un deploy si ves imágenes rotas — eliminará las referencias huérfanas para que vuelvas a subir solo las imágenes necesarias.
            </p>
          </CardContent>
        </Card>

        {/* Help Link Card */}
        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm">Centro de Ayuda</p>
                  <p className="text-xs text-muted-foreground">Guías para admins y profesores</p>
                </div>
              </div>
              <Link to="/admin/help">
                <Button variant="outline" size="sm" data-testid="goto-help-btn">Ver Ayuda</Button>
              </Link>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default AdminSettings;
